---
title: Where a session runs
lede: A micro-VM or this computer, chosen per session and fixed for its whole life.
order: 13
part: Run a task
---

Every session runs in one of two places: a **Micro-VM** or **This computer**. You choose under **Where it runs** on [the task form](/task-form).

![The placement chip](media/newtask-placement.webp)

### Micro-VM

The default, and the isolated one. The session gets its own kernel, a non-root user, its own disk and a network that denies by default. Your repositories are copied in; nothing is mounted, so the agent cannot reach your working tree.

The status bar reads `vm` and the session's id. Hover it for CPU, memory, disk, network and uptime. A **Parked** tag beside it says the machine has stopped for sitting idle, and wakes when you open the session.

![An isolated session's status bar](media/status-isolated.webp)

### This computer

A git worktree of each repository, kept under `~/.airlock`. It starts instantly, but it is your computer: your network, your files, your ports, your user's rights. The agent runs in its CLI's auto mode rather than skipping every prompt.

Claude Code opens here without its folder trust dialog, and reads only the MCP servers Airlock gave it.

The status bar reads `this computer` and the session's id. Click it to open the session's folder in Finder: each repository is a worktree at `~/.airlock/sandboxes/<session id>/workspace/<repository>`, on the branch `session/<session id>`. `git worktree list` in your own repository lists it too. Worktrees the agent makes for itself sit beside it, in `~/.airlock/sandboxes/<session id>/worktrees/`. All of them are removed with the session.

![A local session's status bar](media/status-local.webp)

Windows does not offer this placement: every session there runs in a micro-VM.

### Which to pick

Use a micro-VM. Run on this computer only when the work cannot be done in one: a toolchain missing from the [session image](/session-image), hardware the computer owns, or a repository too heavy to copy.

| | Micro-VM | This computer |
|---|---|---|
| Your working tree | unreachable | reachable |
| Network | deny by default, see [Network](/network) | your computer's |
| Start-up | boots a VM | instant |
| Toolchain | the [session image](/session-image) | what you installed |
| Docker | optional, inside the VM | your Docker |

### When micro-VMs are unavailable

If this computer cannot run a micro-VM, the form does not offer a choice. **Runs in** shows one amber line instead: git worktrees on this computer, why the runtime is unavailable, and that there is no hypervisor boundary and no network policy.

### Defaults

**Workspace settings › Sandbox › Sessions run in** sets the default for one workspace. **Settings › Sandbox › Backend** sets it for every workspace. Either only moves the default.

A task waiting on the [board](/board) can also follow **Workspace**, and is placed when it starts. Once a session has started, its placement never changes.
